import React, { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import '../styles/SignUp.css';

const PLAN_FEATURES = {
  basic:      { en: ['15 travel guides (visa, transport, food, health...)', 'Interactive visa checker', '2026 entrance fees & fares', 'Email support 72h'], fr: ['15 guides de voyage (visa, transports, cuisine, santé...)', 'Vérificateur de visa interactif', 'Tarifs d\'entrée & transports 2026', 'Support email 72h'] },
  premium:    { en: ['All 24 travel guides', 'Budget calculator (TL / EUR / XAF)', 'Itineraries, Cappadocia & day trips', 'Price list & district comparison'], fr: ['Les 24 guides de voyage', 'Calculateur de budget (TL / EUR / XAF)', 'Itinéraires, Cappadoce & excursions', 'Liste de prix & comparatif quartiers'] },
  enterprise: { en: ['All 30 guides', 'Wholesale sourcing & trade-fair calendar', 'Company, work permit, property & banking guides', 'Residence & citizenship by investment'], fr: ['Les 30 guides', 'Achats en gros & calendrier des salons', 'Guides société, permis de travail, immobilier & banque', 'Séjour & citoyenneté par investissement'] },
};

const PLAN_NAMES = { basic: { en: 'Explorer', fr: 'Explorateur' }, premium: { en: 'Navigator', fr: 'Navigateur' }, enterprise: { en: 'Pathfinder', fr: 'Pionnier' } };
const PLAN_ICONS = { basic: '🔍', premium: '🧭', enterprise: '🗺️' };

const COUNTRIES = [
  { value: 'CM', label: '🇨🇲 Cameroun / Cameroon' }, { value: 'SN', label: '🇸🇳 Sénégal / Senegal' },
  { value: 'CI', label: '🇨🇮 Côte d\'Ivoire' }, { value: 'ML', label: '🇲🇱 Mali' },
  { value: 'GN', label: '🇬🇳 Guinée / Guinea' }, { value: 'BJ', label: '🇧🇯 Bénin / Benin' },
  { value: 'TG', label: '🇹🇬 Togo' }, { value: 'BF', label: '🇧🇫 Burkina Faso' },
  { value: 'CD', label: '🇨🇩 Congo RDC / DRC' }, { value: 'CG', label: '🇨🇬 Congo-Brazzaville' },
  { value: 'GA', label: '🇬🇦 Gabon' }, { value: 'GH', label: '🇬🇭 Ghana' },
  { value: 'NG', label: '🇳🇬 Nigeria' }, { value: 'MA', label: '🇲🇦 Maroc / Morocco' },
  { value: 'DZ', label: '🇩🇿 Algérie / Algeria' }, { value: 'TN', label: '🇹🇳 Tunisie / Tunisia' },
  { value: 'ET', label: '🇪🇹 Ethiopia' }, { value: 'KE', label: '🇰🇪 Kenya' },
  { value: 'US', label: '🇺🇸 United States' }, { value: 'CA', label: '🇨🇦 Canada' },
  { value: 'FR', label: '🇫🇷 France' }, { value: 'BE', label: '🇧🇪 Belgique / Belgium' },
  { value: 'CH', label: '🇨🇭 Suisse / Switzerland' }, { value: 'GB', label: '🇬🇧 United Kingdom' },
  { value: 'OTHER', label: '🌍 Other / Autre' },
];

const COUNTRY_CODES = [
  { code: '+237', label: '🇨🇲 +237' }, { code: '+221', label: '🇸🇳 +221' },
  { code: '+225', label: '🇨🇮 +225' }, { code: '+223', label: '🇲🇱 +223' },
  { code: '+229', label: '🇧🇯 +229' }, { code: '+228', label: '🇹🇬 +228' },
  { code: '+226', label: '🇧🇫 +226' }, { code: '+243', label: '🇨🇩 +243' },
  { code: '+241', label: '🇬🇦 +241' }, { code: '+233', label: '🇬🇭 +233' },
  { code: '+234', label: '🇳🇬 +234' }, { code: '+212', label: '🇲🇦 +212' },
  { code: '+251', label: '🇪🇹 +251' }, { code: '+254', label: '🇰🇪 +254' },
  { code: '+1',   label: '🇺🇸 +1' },  { code: '+33',  label: '🇫🇷 +33' },
  { code: '+44',  label: '🇬🇧 +44' },
];

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, loading } = useAuth();

  const state = location.state || {};
  const planAccessLevel = state.planAccessLevel || 'basic';
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const planLabel = PLAN_NAMES[planAccessLevel][lang];
  const paymentReference = state.paymentReference;
  const planIcon = PLAN_ICONS[planAccessLevel];
  const features = PLAN_FEATURES[planAccessLevel][lang];

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', country: '', countryCode: '+237', phone: '' });
  const [showPw, setShowPw]   = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [agreed, setAgreed]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [apiErr, setApiErr]   = useState('');

  const set = (field) => (e) => { setForm(prev => ({ ...prev, [field]: e.target.value })); setErrors(prev => ({ ...prev, [field]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim())  e.firstName = lang === 'fr' ? 'Prénom requis' : 'First name required';
    if (!form.lastName.trim())   e.lastName  = lang === 'fr' ? 'Nom requis' : 'Last name required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = lang === 'fr' ? 'Email invalide' : 'Valid email required';
    if (form.password.length < 6) e.password = lang === 'fr' ? 'Min. 6 caractères' : 'Min. 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = lang === 'fr' ? 'Mots de passe différents' : 'Passwords do not match';
    if (!form.country)           e.country   = lang === 'fr' ? 'Pays requis' : 'Country required';
    if (!form.phone.trim())      e.phone     = lang === 'fr' ? 'Téléphone requis' : 'Phone required';
    if (!agreed)                 e.agreed    = lang === 'fr' ? 'Acceptez les conditions' : 'Please accept the terms';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiErr('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    try {
      await register(form.firstName, form.lastName, form.email, form.password, form.country, `${form.countryCode} ${form.phone}`, paymentReference);
      navigate(planAccessLevel === 'enterprise' ? '/enterprise-dashboard' : planAccessLevel === 'premium' ? '/premium-dashboard' : '/basic-dashboard');
    } catch (err) {
      setApiErr(err?.response?.data?.message || (lang === 'fr' ? 'Erreur d\'inscription. Réessayez.' : 'Registration failed. Please try again.'));
    }
  };

  // An account can only be created with a confirmed payment.
  if (!paymentReference) return <Navigate to="/plans" replace />;

  return (
    <div className="su-page">
      <div className="su-card">
        {/* LEFT */}
        <div className="su-left">
          <div className="su-brand">
            <span className="su-brand-icon">🕌</span>
            <span className="su-brand-name">{lang === 'fr' ? 'Guide Istanbul' : 'Istanbul Guide'}</span>
          </div>
          <div className="su-left-heading">
            <h2>{lang === 'fr' ? 'Plus qu\'une étape !' : 'Almost there!'}</h2>
            <p>{lang === 'fr' ? 'Créez votre compte pour accéder immédiatement à votre tableau de bord.' : 'Create your account to immediately access your dashboard and start your journey.'}</p>
          </div>
          <div className="su-plan-box">
            <div className="su-plan-box-label">{lang === 'fr' ? 'Votre plan' : 'Your plan'}</div>
            <div className="su-plan-name-row">
              <h3>{planIcon} {planLabel}</h3>
              <span className={`su-plan-chip ${planAccessLevel}`}>{lang === 'fr' ? 'Sélectionné' : 'Selected'}</span>
            </div>
            <ul className="su-plan-features">
              {features.map((f, i) => (
                <li key={i}><span className={`su-feat-check ${planAccessLevel}`}>✓</span>{f}</li>
              ))}
            </ul>
          </div>
          <div className="su-trust">
            <div className="su-trust-item">🔒 {lang === 'fr' ? 'Compte sécurisé & chiffré' : 'Secure & encrypted account'}</div>
            <div className="su-trust-item">⚡ {lang === 'fr' ? 'Accès immédiat après inscription' : 'Instant dashboard access'}</div>
            <div className="su-trust-item">🌍 {lang === 'fr' ? 'Support bilingue EN / FR' : 'Bilingual support EN / FR'}</div>
            <div className="su-trust-item">🕌 {lang === 'fr' ? 'Prix & règles mis à jour pour 2026' : 'Prices & rules updated for 2026'}</div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="su-right">
          <div className="su-steps">
            <div className="su-step done"><div className="su-step-num">✓</div>{lang === 'fr' ? 'Choisir' : 'Select'}</div>
            <span className="su-step-sep">›</span>
            <div className="su-step done"><div className="su-step-num">✓</div>{lang === 'fr' ? 'Payer' : 'Pay'}</div>
            <span className="su-step-sep">›</span>
            <div className="su-step active"><div className="su-step-num">3</div>{lang === 'fr' ? 'Compte' : 'Account'}</div>
          </div>
          <div className="su-header">
            <h2>{lang === 'fr' ? 'Créer votre compte' : 'Create Your Account'}</h2>
            <p>{lang === 'fr' ? 'Remplissez le formulaire pour activer votre accès.' : 'Fill in the form below to activate your access.'}</p>
          </div>
          {apiErr && <div className="su-error">⚠️ {apiErr}</div>}
          <form className="su-form" onSubmit={handleSubmit} noValidate>
            <div className="su-row">
              <div className="su-field">
                <label>{lang === 'fr' ? 'Prénom' : 'First Name'} *</label>
                <input className={`su-input ${errors.firstName ? 'invalid' : ''}`} type="text" placeholder={lang === 'fr' ? 'Jean' : 'John'} value={form.firstName} onChange={set('firstName')} autoComplete="given-name" />
                {errors.firstName && <span className="su-field-err">{errors.firstName}</span>}
              </div>
              <div className="su-field">
                <label>{lang === 'fr' ? 'Nom de famille' : 'Last Name'} *</label>
                <input className={`su-input ${errors.lastName ? 'invalid' : ''}`} type="text" placeholder={lang === 'fr' ? 'Dupont' : 'Smith'} value={form.lastName} onChange={set('lastName')} autoComplete="family-name" />
                {errors.lastName && <span className="su-field-err">{errors.lastName}</span>}
              </div>
            </div>
            <div className="su-field">
              <label>{lang === 'fr' ? 'Adresse email' : 'Email Address'} *</label>
              <input className={`su-input ${errors.email ? 'invalid' : ''}`} type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} autoComplete="email" />
              {errors.email && <span className="su-field-err">{errors.email}</span>}
            </div>
            <div className="su-row">
              <div className="su-field">
                <label>{lang === 'fr' ? 'Mot de passe' : 'Password'} *</label>
                <div className="su-pw-wrap">
                  <input className={`su-input ${errors.password ? 'invalid' : ''}`} type={showPw ? 'text' : 'password'} placeholder={lang === 'fr' ? 'Min. 6 caractères' : 'Min. 6 characters'} value={form.password} onChange={set('password')} autoComplete="new-password" />
                  <button type="button" className="su-pw-toggle" onClick={() => setShowPw(v => !v)}>{showPw ? '🙈' : '👁️'}</button>
                </div>
                {errors.password && <span className="su-field-err">{errors.password}</span>}
              </div>
              <div className="su-field">
                <label>{lang === 'fr' ? 'Confirmer' : 'Confirm Password'} *</label>
                <div className="su-pw-wrap">
                  <input className={`su-input ${errors.confirmPassword ? 'invalid' : ''}`} type={showCpw ? 'text' : 'password'} placeholder={lang === 'fr' ? 'Répétez' : 'Repeat password'} value={form.confirmPassword} onChange={set('confirmPassword')} autoComplete="new-password" />
                  <button type="button" className="su-pw-toggle" onClick={() => setShowCpw(v => !v)}>{showCpw ? '🙈' : '👁️'}</button>
                </div>
                {errors.confirmPassword && <span className="su-field-err">{errors.confirmPassword}</span>}
              </div>
            </div>
            <div className="su-field">
              <label>{lang === 'fr' ? 'Pays d\'origine' : 'Country of Origin'} *</label>
              <select className={`su-select ${errors.country ? 'invalid' : ''}`} value={form.country} onChange={set('country')}>
                <option value="">{lang === 'fr' ? '— Sélectionnez votre pays —' : '— Select your country —'}</option>
                {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {errors.country && <span className="su-field-err">{errors.country}</span>}
            </div>
            <div className="su-field">
              <label>{lang === 'fr' ? 'Numéro de téléphone' : 'Phone Number'} *</label>
              <div className="su-phone-row">
                <select className="su-code-select" value={form.countryCode} onChange={set('countryCode')}>
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                <input className={`su-input ${errors.phone ? 'invalid' : ''}`} type="tel" placeholder={lang === 'fr' ? 'Ex : 6 70 00 00 00' : 'e.g. 677 000 000'} value={form.phone} onChange={set('phone')} autoComplete="tel" />
              </div>
              {errors.phone && <span className="su-field-err">{errors.phone}</span>}
            </div>
            <label className="su-terms">
              <input type="checkbox" checked={agreed} onChange={e => { setAgreed(e.target.checked); setErrors(prev => ({ ...prev, agreed: '' })); }} />
              <span>
                {lang === 'fr' ? <>J'accepte les <a href="#about">conditions</a> de Guide Istanbul.</> : <>I agree to Istanbul Guide's <a href="#about">Terms of Service</a>.</>}
                {errors.agreed && <span className="su-field-err" style={{ display: 'block', marginTop: 4 }}>{errors.agreed}</span>}
              </span>
            </label>
            <button type="submit" className={`su-submit ${planAccessLevel}`} disabled={loading}>
              {loading ? (lang === 'fr' ? '⏳ Création du compte...' : '⏳ Creating account...') : (lang === 'fr' ? `🚀 Créer mon compte & accéder au tableau de bord` : `🚀 Create Account & Access Dashboard`)}
            </button>
          </form>
          <p className="su-signin-link">
            {lang === 'fr' ? 'Déjà un compte ?' : 'Already have an account?'}{' '}<Link to="/signin">{lang === 'fr' ? 'Se connecter' : 'Sign in here'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
